import React, { useMemo } from 'react';
import { Flex, Image } from '@mantine/core';
import { DataInputIconsContext, type IconMap } from './context';
import { DatasetType, StepType } from '@/types/automationFields';
import useRetrieveStaticDatasets from '@/hooks/api/datasets/static/useRetrieveStaticDatasets';
import useRetrieveLinkedDatasets from '@/hooks/api/datasets/linked/useRetrieveLinkedDatasets';
import useStepIntegration from '@/hooks/useStepIntegration';
import useGetStepTypeConfigAndLabel from '@/pages/Editor/components/EditorStepCard/hooks/useGetStepTypeConfigAndLabel';
import StepCardIcon from '@/components/StepCard/StepCardIcon';
import type { AutomationStep } from '@/types/automationSteps';
import {
  fakeDataset,
  getIconForConfigFactory,
  makeDynamicOptionValue,
  makeEmptyOptionValue,
  makeLinkedOptionValue,
  makeStaticOptionValue,
  normalizeDynamicValue,
} from './utils';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import IntegrationLogo from '@/pages/Integrations/components/IntegrationLogo.tsx';
// ⬇️ adjust this import path to wherever your function actually lives
import { datasetToFileTypeAndIcon } from '@/pages/AutomationPage/components/DatasetItemUtils.ts';
import { isDirectory } from '@/utils/datasetTypes.ts';
import AppIcon from '@/components/AppIcon.tsx';
import { HiOutlineDocument, HiOutlineFolder } from 'react-icons/hi2';
import { Dataset } from '@/types/datasets.ts';
import { getAllStepChildren, isStepWithChildren } from '@/utils/stepUtils.ts';

// Flatten all steps (including children)
function flattenSteps(steps: AutomationStep[]): AutomationStep[] {
  const out: AutomationStep[] = [];
  const walk = (s: AutomationStep) => {
    out.push(s);
    if (isStepWithChildren(s)) getAllStepChildren(s).forEach(walk);
  };
  steps.forEach(walk);
  return out;
}

// Icon for a dynamic step (replicates StepCardTitle's visual without labels)
function DynamicStepIcon({ step, size = 18 }: { step: AutomationStep; size?: number }) {
  const integrationConfig = React.useMemo(() => {
    if (
      (step.step_type === StepType.Trigger && (step as any).trigger_type === 'external') ||
      step.step_type === StepType.Dispatch
    ) {
      return (step as any).integration_config;
    }
    return undefined;
  }, [step]);

  const { selectedIntegrationProvider, selectedIntegrationAction } = useStepIntegration(
    step.step_type,
    integrationConfig?.integration_id || null,
    integrationConfig?.action_type || null,
  );

  const { config } = useGetStepTypeConfigAndLabel(step.step_type, selectedIntegrationAction);

  return (
    <StepCardIcon
      Icon={config.icon}
      CustomIcon={config.svg}
      bg={config.iconBackgroundColor}
      iconColor={config.iconColor}
      integrationProvider={selectedIntegrationProvider}
      size={size}
      style={{ borderRadius: '4px' }}
    />
  );
}

// Render an icon element for a Dataset, handling string URLs vs components
function renderDatasetIcon(dataset: Dataset, size: number) {
  return (
    <Flex
      style={{
        borderRadius: '4px',
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.04), 0 3px 6px -2px rgba(0, 0, 0, 0.02)',
      }}
    >
      {_renderDatasetIcon(dataset, size)}
    </Flex>
  );
}

function _renderDatasetIcon(dataset: Dataset, size: number) {
  const { icon } = datasetToFileTypeAndIcon(dataset);
  if (icon) {
    return (
      <Image src={icon} height={size} fit="contain" w="unset" style={{ borderRadius: '4px' }} />
    );
  } else if (isDirectory(dataset)) {
    return <AppIcon Icon={HiOutlineFolder} iconProps={{ color: '#757575' }} size={size} />;
  }
  // Default gray document icon if no dataset type detected
  return <AppIcon Icon={HiOutlineDocument} iconProps={{ color: '#757575' }} size={size} />;
}

export function DataInputIconsProvider({
  children,
  size = 18,
}: {
  children: React.ReactNode;
  size?: number;
}) {
  const { data: staticDatasets, isPending: loadingStatic } = useRetrieveStaticDatasets();
  const { data: linkedDatasets, isPending: loadingLinked } = useRetrieveLinkedDatasets();
  const { data: latestVersion, isPending: loadingAutomation } =
    useRetrieveLatestAutomationVersion();

  const staticMap = useMemo(() => {
    const map: IconMap = new Map();
    staticDatasets?.results.forEach((sd) => {
      const value = makeStaticOptionValue(sd.id);
      const node = renderDatasetIcon(sd.dataset as unknown as Dataset, size);
      map.set(value, node);
    });
    return map;
  }, [staticDatasets?.results, size]);

  const linkedMap = useMemo(() => {
    const map: IconMap = new Map();
    linkedDatasets?.results.forEach((ld) => {
      const value = makeLinkedOptionValue(ld.id);
      const providerName = ld.integration.integration_type as string;
      map.set(value, <IntegrationLogo key={value} name={providerName} />);
    });
    return map;
  }, [linkedDatasets?.results]);

  const dynamicMap = useMemo(() => {
    const map: IconMap = new Map();
    if (!latestVersion?.steps) return map;
    const allSteps = flattenSteps(latestVersion.steps);
    allSteps.forEach((step) => {
      const key = makeDynamicOptionValue(step.step_id);
      map.set(key, <DynamicStepIcon key={key} step={step} size={size} />);
    });
    return map;
  }, [latestVersion?.steps, size]);

  const emptyMap = useMemo(() => {
    const map: IconMap = new Map();
    [DatasetType.File, DatasetType.Dataframe, DatasetType.Json, DatasetType.Directory].forEach(
      (t) => {
        const value = makeEmptyOptionValue(t);
        const fake = fakeDataset(`Empty ${t} Dataset`, t);
        const node = renderDatasetIcon(fake, size);
        map.set(value, node);
      },
    );
    return map;
  }, [size]);

  const iconsByOptionValue = useMemo(
    () => new Map([...emptyMap, ...dynamicMap, ...linkedMap, ...staticMap]),
    [emptyMap, dynamicMap, linkedMap, staticMap],
  );

  const getIconForOptionValue = React.useCallback(
    (value: string) => {
      if (!value) return null;
      const normalized = value.startsWith('dynamic') ? normalizeDynamicValue(value) : value;
      return iconsByOptionValue.get(normalized) || null;
    },
    [iconsByOptionValue],
  );

  const getIconForConfig = React.useMemo(
    () => getIconForConfigFactory(getIconForOptionValue),
    [getIconForOptionValue],
  );

  const isLoading = loadingStatic || loadingLinked || loadingAutomation;

  return (
    <DataInputIconsContext.Provider
      value={{ iconsByOptionValue, getIconForOptionValue, getIconForConfig, isLoading }}
    >
      {children}
    </DataInputIconsContext.Provider>
  );
}
